"use client";

/**
 * The competitor's own favicon, over a monogram that shows if it does not arrive.
 *
 * WHY NOT JUST AN <img>. Three separate things have to be true before a third-party mark is safe to
 * put on this site, and only one of them is about whether the image loads:
 *
 *   1. THE BROWSER MUST NOT TALK TO A FAVICON SERVICE. Hot-linking one tells that service which
 *      askFinz pages are being read and which companies we compare ourselves to, for every visitor.
 *      app/api/favicon/route.ts exists to prevent exactly this and says so in its own header; the
 *      price table on two compare pages was bypassing it and hot-linking Google's endpoint, which
 *      301s to a gstatic host our CSP does not list.
 *   2. NOTHING IS COMMITTED. No competitor's trademarked logo file enters the repo — the proxy
 *      fetches at request time and the CDN caches the result.
 *   3. THE TILE CAN NEVER BE BLANK. It renders a monogram first and only reveals the favicon on
 *      top once one has genuinely arrived.
 *
 * DETECTING "NO FAVICON" NEEDS naturalWidth, NOT onError. The proxy answers a domain it cannot
 * resolve with a 1x1 transparent GIF so the row never breaks — a 200 with a valid image body. So
 * `onError` never fires for a missing mark; what distinguishes it is that the decoded image is one
 * pixel wide. Anything 2px or narrower is treated as nothing and the monogram stands.
 */

import { useCallback, useState } from "react";
import { BrandMonogram } from "@/components/marketing/brand-monogram";

export function FaviconTile({
  name,
  domain,
  src,
  mono,
  className = "",
  imgClassName = "h-10 w-10",
}: {
  name: string;
  /** Company domain. Omitted for a page that compares a CATEGORY rather than a company. */
  domain?: string;
  /** A first-party image, served from this origin — our own mark. Takes precedence over `domain`
   *  and needs none of the machinery below: nothing to proxy, no trademark to keep out of the
   *  repo, and no 1x1-GIF sentinel to detect, because the file either serves or the site is down.
   *  The monogram is skipped rather than left sitting invisible underneath. */
  src?: string;
  mono?: string;
  className?: string;
  imgClassName?: string;
}) {
  const [shown, setShown] = useState(false);

  if (src) {
    return (
      <div className={`relative grid place-items-center ${className}`}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt="" aria-hidden decoding="async" className={`${imgClassName} object-contain`} />
      </div>
    );
  }

  /* Runs when the element is attached. `complete` is true for an image that finished before this
     component hydrated, which is the common case here and the one onLoad cannot see. */
  const check = useCallback((el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 2) setShown(true);
  }, []);

  return (
    <div className={`relative grid place-items-center ${className}`}>
      <BrandMonogram
        name={name}
        mono={mono}
        className={`h-full w-full rounded-2xl shadow-sm ${shown ? "invisible" : ""}`}
      />

      {domain && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={check}
          src={`/api/favicon?d=${encodeURIComponent(domain)}`}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            if (e.currentTarget.naturalWidth > 2) setShown(true);
          }}
          className={`absolute ${imgClassName} object-contain ${shown ? "" : "opacity-0"}`}
        />
      )}
    </div>
  );
}
